"use strict";

// app/javascript/controllers/fee_calculator_controller.js
import { Controller } from "@hotwired/stimulus"
import { post } from "@rails/request.js"
import debounce from "lodash/debounce"

export default class extends Controller {
  static targets = [
    "amount", // input
    "includeFees",
    "feeAmount",
    "totalAmount",
    "feeOption", // Button to select fee option
    "feeDetails" // Optional fee details
  ]

  static values = {
    calculatorUrl: String,
    paymentMethod: String, // bank_account OR credit_card
  }

  initialize() {
    this.handleAmountChange = this.handleAmountChange.bind(this)
    this.handlePaymentMethodChange = this.handlePaymentMethodChange.bind(this)
    this.calculate = debounce(this.calculate.bind(this), 100)
  }

  connect() {
    this.currentAmount = this.amountTarget.value
    this.abortController = null

    // Set initial selected button
    this.feeOptionTargets.forEach(option => {
      if (option.dataset.includeCosts === this.includeFeesTarget.value) {
        option.classList.add("selected")
      } else {
        option.classList.remove("selected")
      }
    })

    this.calculate();

    // Listen for amount changes from the amount controller
    this.element.addEventListener("amount:changed", this.handleAmountChange)
    // Listen for payment method changes
    this.element.addEventListener("payment-method:changed", this.handlePaymentMethodChange)
  }

  disconnect() {
    this.element.removeEventListener("amount:changed", this.handleAmountChange)
    this.element.removeEventListener("payment-method:changed", this.handlePaymentMethodChange)

    // Abort if user navigates away
    if (this.abortController) {
      this.abortController.abort();
    }
  }

  // Calculate fees based on the current amount and payment method
  // and update the display.
  // This method is debounced to prevent multiple requests.
  async calculate() {
    // Abort any existing request
    if (this.abortController) {
      this.abortController.abort()
    }

    this.abortController = new AbortController()

    // Reset display if amount or payment method is missing
    if (this.currentAmount === undefined || this.currentAmount === null || this.paymentMethodValue === undefined || this.currentAmount === 0 || this.currentAmount === "" || this.currentAmount === "0.00") {
      this.resetDisplay()
      return
    }

    // Send request to calculate fees
    const formData = new FormData()
    formData.append("amount", this.currentAmount)
    formData.append("payment_method", this.paymentMethodValue)
    formData.append("fees_should_be_paid_by_giver", this.includeFeesTarget.value)

    try {
      const response = await post(this.calculatorUrlValue, { body: formData, signal: this.abortController.signal })
      if (response.ok) {
        const data = await response.json
        this.updateDisplay(data)
      } else {
        this.resetDisplay();
        console.error("Fee calculation failed:")
      }
    } catch (error) {
      if (error.name === "AbortError") {
        console.log("Previous request aborted, new one started.");
      } else {
        this.resetDisplay();
        console.error("Fee calculation failed:", error)
      }
    }
  }

  // Reset the fee display
  resetDisplay() {
    this.feeAmountTargets.forEach(target => {
      target.textContent = "$0.00"
    })

    if (this.hasTotalAmountTarget) {
      this.totalAmountTarget.textContent = "$0.00"
    }

    if (this.hasFeeDetailsTarget) {
      this.feeDetailsTarget.innerHTML = ''
    }

    // Dispatch event that calculation is complete
    const event = new CustomEvent("fees:error", {
      detail: {
        amount: this.currentAmount,
        payment_method: this.paymentMethodValue,
        include_fees: this.includeFeesTarget.value,
      },
      bubbles: true
    })
    this.includeFeesTarget.dispatchEvent(event)
  }

  // Update the displayed fees with the calculated data
  updateDisplay(data) {
    const fees = Math.max(
      data.converted_fees_paid_by_giver,
      data.converted_fees_paid_by_church
    )

    const formattedFees = new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD"
    }).format(fees)

    this.feeAmountTargets.forEach(target => {
      target.textContent = formattedFees
    })

    // Update fee details if available
    if (this.hasFeeDetailsTarget) {
      this.feeDetailsTarget.innerHTML = this.feeOptionTargets
        .find(option => option.classList.contains("selected"))
        .dataset
        .details
        .replace("{{feeAmount}}", formattedFees)
    }

    if (this.hasTotalAmountTarget) {
      this.totalAmountTarget.textContent = new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "USD"
      }).format(data.converted_amount_paid_by_giver)
    }

    // Dispatch event that calculation is complete
    const event = new CustomEvent("fees:calculated", {
      detail: {
        fees,
        total: data.converted_amount_paid_by_giver
      },
      bubbles: true
    })
    this.includeFeesTarget.dispatchEvent(event)
  }

  // Public method to trigger a recalculation
  handleFeeOptionClick(event) {
    event.preventDefault()

    this.feeOptionTargets.forEach(option =>
      option.classList.remove("selected")
    )

    const selectedOption = event.currentTarget
    selectedOption.classList.add("selected")
    this.includeFeesTarget.value = selectedOption.dataset.includeCosts === "true"

    this.calculate()
  }

  // Event handlers for amount and payment method changes
  handleAmountChange(event) {
    this.currentAmount = event.detail.amount
    this.calculate()
  }

  handlePaymentMethodChange(event) {
    this.paymentMethodValue = event.detail.method
    this.calculate()
  }
}
